import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRightLeft,
  BarChart,
  Bell,
  Camera,
  Home,
  Inbox,
  Landmark,
  LayoutTemplate,
  Library,
  ListChecks,
  PanelLeft,
  PanelRight,
  PiggyBank,
  Puzzle,
  Receipt,
  RefreshCw,
  Settings,
  Target,
} from 'lucide-react';


import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
useSidebar,
} from '@/components/ui/sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from '@/components/ui/badge';
import { NavUser } from './NavUser';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { NotificationBell } from './NotificationBell';
import { FloatingActionButton } from '@/components/common/FloatingActionButton';
import { FABProvider } from '@/components/common/FABContext';
import { NetworkStatusBanner } from '@/components/common/NetworkStatusBanner';
import { useAppSelector } from '@/features/hooks';
import { useThemeCustomization } from '@/hooks/useThemeCustomization';
import { cn } from "@/lib/utils";

const menuItems = [
  { href: '/dashboard', label: 'Overview', icon: Home },
  { href: '/transactions', label: 'Transactions', icon: ArrowRightLeft },
  { href: '/accounts', label: 'Accounts', icon: Landmark },
  { href: '/budgets', label: 'Budgets', icon: PiggyBank },
  { href: '/goals', label: 'Goals', icon: Target },
  { href: '/insights', label: 'Insights', icon: BarChart },
];

const advancedMenuItems = [
  { href: '/dashboards', label: 'Custom Dashboards', icon: LayoutTemplate },
  { href: '/widgets', label: 'Widget Library', icon: Library },
  { href: '/review', label: 'Review Inbox', icon: Inbox },
  { href: '/subscriptions', label: 'Subscriptions', icon: RefreshCw },
  { href: '/bills', label: 'Bills', icon: Receipt },
  { href: '/receipts', label: 'Receipts', icon: Camera },
  { href: '/rules', label: 'Rules', icon: ListChecks },
  { href: '/notifications', label: 'Notifications', icon: Bell },
  { href: '/extensions', label: 'Extensions', icon: Puzzle },
];

/**
 * Sidebar collapse / expand toggle (v3.3+).
 *
 * Bidirectional icon button that controls the primary navigation sidebar.
 *
 * Render location: ALWAYS rendered inside <SidebarHeader> as the first
 * child of the inner flex row, followed by the FinTrack brand cell.
 * The button is on the LEFT; the brand fills remaining space (flex-1).
 *
 * Visibility (v3.5): ALWAYS visible — no longer hidden when `pinned === true`.
 *   Rationale: hiding the toggle on desktop (default pinned=true) made the
 *   sidebar effectively non-collapsible through UI. Users could only collapse
 *   via the Cmd/Ctrl+B shortcut, which is not discoverable.
 *
 * Click behavior:
 *   - If `pinned === true`: clicking unpins (one-way, makes sidebar collapsible).
 *   - Otherwise: clicking toggles expand/collapse via `toggleSidebar()`.
 *
 * Sizing: 40×40 (matches all menu buttons and the avatar).
 *
 * Accessibility (W3C APG Disclosure Pattern):
 *   - `aria-label` swaps "Collapse sidebar" / "Expand sidebar" / "Unpin sidebar".
 *   - `aria-expanded` reflects the effective state.
 *   - `aria-controls="primary-sidebar"` references <Sidebar id>.
 *
 * Keyboard: handled by shadcn `useSidebar().toggleSidebar`, fires Ctrl/Cmd+B.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/
 * @see https://ui.shadcn.com/docs/components/sidebar
 */
export function SidebarCollapseToggle({ className }: { className?: string }) {
  const { toggleSidebar, togglePinned, state, pinned, isMobile } = useSidebar();
  const isCollapsed = state === "collapsed";

  const handleClick = () => {
    if (pinned) {
      // When pinned, the sidebar is locked open. Make it collapsible first.
      togglePinned();
      return;
    }
    toggleSidebar();
  };

  const ariaLabel = pinned
    ? "Unpin sidebar"
    : isCollapsed
      ? "Expand sidebar"
      : "Collapse sidebar";

// T-SIDEBAR-MOBILE-UX Fix A: suppress tooltip on mobile to avoid it covering the brand logo.
// The <Button> stays interactive; only the <Tooltip> wrapper is skipped.
const toggleButton = (
<Button
key="sidebar-collapse-toggle"
data-sidebar="collapse-toggle"
data-state={state}
variant="ghost"
size="icon"
aria-label={ariaLabel}
aria-expanded={!isCollapsed && !pinned}
aria-controls="primary-sidebar"
onClick={handleClick}
className={cn(
"h-11 w-11 min-h-[44px] min-w-[44px] rounded-lg",
"transition-[background-color,color,box-shadow,transform] duration-200 ease-out motion-reduce:transition-none",
"hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[inset_0_0_0_1px_hsl(var(--sidebar-border))] active:scale-[0.98] motion-reduce:active:scale-100",
"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
className,
)}
>
{isCollapsed ? (
<PanelRight className="size-4" aria-hidden="true" />
) : (
<PanelLeft className="size-4" aria-hidden="true" />
)}
</Button>
);

if (isMobile) {
// On mobile the sidebar lives inside a Sheet; tooltips would render over
// the brand logo and are unnecessary (the button label is already readable).
return toggleButton;
}

return (
<Tooltip>
<TooltipTrigger asChild>{toggleButton}</TooltipTrigger>
<TooltipContent side="right" align="center">
{ariaLabel}
</TooltipContent>
</Tooltip>
);
}
/**
 * SidebarHeaderContent (v3.4)
 *
 * Renders the sidebar header row (toggle + optional brand) using conditional
 * rendering based on sidebar state. Extracted into its own component so it
 * can call useSidebar() from within the SidebarProvider's context.
 */
function SidebarHeaderContent() {
  // R37: Show brand only when sidebar is expanded. On mobile the `state`
  // is always "collapsed" (desktop derivation), so also show it when the
  // mobile Sheet is open.
  const { state, isMobile, openMobile } = useSidebar();
  const brandHidden = (state === "collapsed" && !(isMobile && openMobile)) || (isMobile && !openMobile);
  return (
    <SidebarHeader className="h-14 px-2 py-0">
      <SidebarMenu>
        <SidebarMenuItem>
          <div
            data-sidebar="header-row"
            className="flex h-14 min-h-0 w-full flex-row items-center gap-3"
          >
            <SidebarCollapseToggle className="flex-shrink-0" />
            <div
              data-sidebar="brand"
              data-brand-hidden={brandHidden ? "true" : "false"}
              aria-hidden={brandHidden}
              className={cn(
                "flex min-h-11 flex-1 items-center justify-start overflow-hidden transition-[width,opacity] duration-200 ease-out motion-reduce:transition-none",
                brandHidden ? "w-0 opacity-0 pointer-events-none" : "w-auto opacity-100"
              )}
            >
              <SidebarMenuButton
                size="lg"
                isActive={false}
                asChild
                tooltip={undefined}
                className="w-full !py-0 data-[brand-hidden=true]:hidden"
              >
                <Link to="/dashboard" data-testid="sidebar-brand-link">
                  <div
                    data-sidebar="brand-icon"
                    className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground"
                  >
                    <Landmark className="size-5" aria-hidden="true" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                    <span className="font-semibold whitespace-nowrap">FinTrack</span>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">Personal Finance</span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </div>
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
  );
}
/**
 * T-SIDEBAR-MOBILE-UX Fix C: Auto-close the mobile Sheet on route change.
 *
 * This is a "headless" helper component (renders nothing) that must live
 * INSIDE <SidebarProvider> so it can call useSidebar(). On mobile, whenever
 * the route pathname changes we close the Sheet — users tapped a nav link,
 * the destination page is now visible behind the Sheet, so leaving the
 * Sheet open obscures the result.
 *
 * Renders nothing on desktop (isMobile is false).
 */
function MobileSheetAutoCloser({ pathname }: { pathname: string }) {
  const { isMobile, setOpenMobile } = useSidebar();
  React.useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [pathname, isMobile, setOpenMobile]);
  return null;
}

/** Inner layout that consumes FABContext */
function AppLayoutInner({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const allNavItems = [...menuItems, ...advancedMenuItems, { href: '/settings', label: 'Settings', icon: Settings }];
  const currentItem = allNavItems.find((item) => pathname === item.href || pathname.startsWith(item.href + '/'));
  const pageTitle = currentItem?.label || 'FinTrack';

  // T155P1: Live review queue count for sidebar badge
  const reviewTotal = useAppSelector((s) => s.review.queue?.total ?? 0);

// [T277-PY] Mount the theme hook globally so the sidebar accent CSS vars
// (--sidebar-primary, --sidebar-accent, --primary, --ring) are written on
// every protected route. Without this, navigating directly to /dashboard
// (or any non-settings route) left the CSS at the index.css defaults since
// useThemeCustomization was previously invoked only from the Settings page.
// `void` discards the hook's return object — we only need its side effect.
void useThemeCustomization();


  return (
    <SidebarProvider>
      <MobileSheetAutoCloser pathname={pathname} />
      <Sidebar id="primary-sidebar" variant="inset" collapsible="icon">
        <SidebarHeaderContent />
        <SidebarContent className="gap-1 scrollbar-none sidebar-scroll-fade overflow-x-hidden overflow-y-auto overscroll-contain group-data-[collapsible=icon]:overflow-x-hidden group-data-[collapsible=icon]:overflow-y-auto">
          <SidebarGroup className="pt-1">
            <SidebarGroupLabel>Application</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {menuItems.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === item.href || pathname.startsWith(item.href + '/')}
                      data-testid={`nav-${item.href.replace(/^\//,'')}`}
                      tooltip={item.label}
                    >
                      <Link to={item.href}>
                        <item.icon />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarGroup className="pt-1">
            <SidebarGroupLabel>Advanced</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {advancedMenuItems.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === item.href || pathname.startsWith(item.href + '/')}
                      data-testid={`nav-${item.href.replace(/^\//,'')}`}
                      tooltip={item.href === '/review' && reviewTotal > 0 ? `Review Inbox (${reviewTotal})` : item.label}
                    >
                      <Link to={item.href}>
                        <item.icon />
                        <span>{item.label}</span>
                        {item.href === '/review' && reviewTotal > 0 && (
                          <Badge
                            variant="destructive"
                            className="ml-auto text-[10px] px-1.5 py-0 min-w-[1.25rem] h-4"
                          >
                            {reviewTotal}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarGroup className="pt-1">
            <SidebarGroupLabel>Settings</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === '/settings' || pathname.startsWith('/settings/')}
                    data-testid="nav-settings"
                    tooltip="Settings"
                  >
                    <Link to="/settings">
                      <Settings />
                      <span>Settings</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <NavUser />
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="flex flex-col min-w-0 md:h-[calc(100svh-1rem)] h-svh overflow-hidden transition-[margin] duration-300 ease-in-out">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b border-border/40 bg-background/80 px-3 md:px-4 backdrop-blur-md">
          <div className="flex items-center gap-2">
            {/* Mobile-only hamburger; desktop collapse toggle lives in <SidebarHeader> */}
            <SidebarTrigger className="md:hidden -ml-1 min-h-[44px] min-w-[44px]" />
            <Separator orientation="vertical" className="mr-2 h-4 hidden md:block" />
            <Breadcrumb data-testid="app-header-page-breadcrumb">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage className="inline-flex min-h-9 items-center text-sm text-muted-foreground">
                    <span>{pageTitle}</span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <ThemeToggle />
          </div>
        </header>
        <NetworkStatusBanner />
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 p-4 md:p-6 scrollbar-elegant"><div className="animate-in fade-in-0 duration-200 h-full flex flex-col">{children}</div></main>
      </SidebarInset>

      {/* [T-FAB] Floating Action Button — main navigator */}
      <FloatingActionButton />
    </SidebarProvider>
  );
}

/** Public export — wraps the inner layout with FABProvider */
export function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <FABProvider>
      <AppLayoutInner>{children}</AppLayoutInner>
    </FABProvider>
  );
}
